import { RefObject, createRef, useCallback, useEffect, useMemo, useState } from 'react'
import { CmsLink } from '../../../cmsTypes/cmsLink'
import { cmsDataService } from '../../../services/cms.data.service'
import { DropdownOption } from '@vista-digital/occ-ui'

interface NavItem extends CmsLink {
    isCollapsed: boolean
    width: number
    ref?: RefObject<HTMLDivElement>
}

const ZERO_WIDTH_SPACE = '\u200B'

export const useCollapsibleNavItems = (
    navContainer: RefObject<HTMLDivElement>,
    moreButton: RefObject<HTMLDivElement>,
    headerContainer: RefObject<HTMLDivElement>
) => {
    const [isMobile, setIsMobile] = useState<boolean>(false)

    const { mainNavigation } = cmsDataService.get().header

    // When we're about to show the more button, we need to take its width into account before its ref is available.
    // This value comes from inspecting the more button when it is visible, assuming that its text, and therefore width, never changes.
    const optimisticMoreButtonWidth = 100

    const [navItems, setNavItems] = useState<Array<NavItem>>(
        mainNavigation.map(x => ({
            ...x,
            isCollapsed: false,
            width: 0,
            ref: createRef(),
        }))
    )

    useEffect(() => {
        // console.debug('Nav item resize effect triggered')

        // Update the nav width on first load
        updateNavItems()

        window.addEventListener('resize', updateNavItems)
        return () => window.removeEventListener('resize', updateNavItems)
    }, [])

    const moreDropdownOptions: DropdownOption[] = (() => {
        const options = navItems.map(item => {
            if (item.isCollapsed) {
                const dropdownOption: DropdownOption = {
                    text: item.name,
                    // Dropdown value must be a string, but we need 2 values for url and target,
                    // so we'll join them with a zero width space and split on a zero width character below
                    value: `${item.url}${ZERO_WIDTH_SPACE}${item.target}`,
                }

                return dropdownOption
            }
        })

        return options.filter(x => !!x) as DropdownOption[]
    })()

    const updateNavItems = () => {
        const isGoingToBeMobile = window.innerWidth < 992
        setIsMobile(isGoingToBeMobile)

        setNavItems(currentNavItems => {
            if (isGoingToBeMobile) {
                return currentNavItems.map(navItem => ({ ...navItem, isCollapsed: false }))
            }

            const sumOfGapWidths =
                (headerContainer.current?.offsetWidth ?? 0) - (navContainer.current?.offsetWidth ?? 0)
            const moreButtonWidth = moreButton?.current?.offsetWidth ?? 0

            if (currentNavItems.some(x => x.isCollapsed) && moreButtonWidth === 0) {
                // console.log(
                //     'Update nav items triggered, but some nav items are collapsed when more button does not have a width. Skipping this check and waiting for next call.',
                //     currentNavItems,
                //     moreButtonWidth
                // )
                return currentNavItems
            }

            if (!currentNavItems.some(x => x.isCollapsed) && moreButtonWidth > 0) {
                // console.log(
                //     'Detected that no nav items are collapsed, but more button has a width. Skipping this check and waiting for next call.',
                //     currentNavItems,
                //     moreButtonWidth
                // )
                return currentNavItems
            }

            const modifiedNavItems: Array<NavItem> = []

            // Calculate the available nav width based on screen size, factoring in the rest of the header items

            const windowInnerWidth = window.innerWidth
            const headerContainerPadding = 24 * 2

            const maxNavItemWidth = windowInnerWidth - moreButtonWidth - sumOfGapWidths - headerContainerPadding

            console.debug('Updating Nav Items', {
                maxNavItemWidth,
                windowInnerWidth,
                moreButtonWidth,
                sumOfGapWidths,
            })

            let totalNavWidth = 0
            let willMoreButtonShow = false

            // Loop to see how many nav items fit on desktop displays

            for (let i = 0; i < currentNavItems.length; i++) {
                const item = currentNavItems[i]
                const itemWidth = item.ref?.current?.offsetWidth ?? 0

                const moreButtonNeedsDeductingFromMaxNavItemWidth = moreButtonWidth === 0 && willMoreButtonShow

                const needsToBeCollapsed =
                    totalNavWidth + itemWidth >
                    maxNavItemWidth - (moreButtonNeedsDeductingFromMaxNavItemWidth ? optimisticMoreButtonWidth : 0)

                if (!needsToBeCollapsed) {
                    totalNavWidth += itemWidth
                }

                if (needsToBeCollapsed && !willMoreButtonShow) {
                    willMoreButtonShow = true

                    // When the more button is added, collapse the previous nav item, otherwise the 'More' button width may be smaller than the current nav items width, which then causes a flicker as this logic will be unable to determine if showing the dropdown makes the nav smaller or not.
                    if (i > 1) {
                        modifiedNavItems[i - 1].isCollapsed = true
                    }
                }

                // console.debug('Adding nav item', {
                //     text: item.ref?.current?.textContent,
                //     needsToBeCollapsed,
                //     itemWidth,
                //     totalNavWidth,
                //     maxNavItemWidth,
                // })

                // When nav width exceeds available width, collapse

                modifiedNavItems.push({
                    ...item,
                    isCollapsed: needsToBeCollapsed,
                })
            }

            // console.log('New nav items', modifiedNavItems)

            return modifiedNavItems
        })
    }

    return { navItems, moreDropdownOptions, isMobile }
}
